import clsx from "clsx";
import type { FC } from "react";
import { UseFormRegisterReturn } from "react-hook-form";
import "./OutcomeAssessment.scss";

import { displayOutcomeName } from "components/outcomes/helpers";
import { t } from "i18n/i18n";
import { OutcomeChip } from "mds/components/OutcomeChip";
import { SquareIconButton } from "mds/components/buttons/SquareIconButton";
import { ScoreIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import { NestedPageOutcomeType, selectPageOutcomeRubric } from "store/selectors";
import { BUTTON_EVENT_CATEGORY, trackEvent } from "utils/amplitude";
import { scoreDescriptionBackgroundColor } from "utils/outcome";

interface OutcomeAssessmentProps {
  pageOutcome: NestedPageOutcomeType;
  score: number | null;
  setScore: (pageOutcomeId: string, score: number | null) => void;
  register: UseFormRegisterReturn;
  disabled?: boolean;
  readOnly?: boolean;
}

export const OutcomeAssessment: FC<OutcomeAssessmentProps> = ({
  pageOutcome,
  score,
  setScore,
  register,
  readOnly = false,
  disabled = false,
}) => {
  // TODO: this is very similar to the AssessmentItem component used by AssessmentDisplayView.
  // We should consolidate these implementations. (JN)
  const rubric = useAppSelector((s) => selectPageOutcomeRubric(s, pageOutcome.id));
  const rubricLevel = rubric.find((level) => level.score === score);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex justify-between">
        <OutcomeChip title={displayOutcomeName(pageOutcome.outcome)} />
        <RubricButtons
          disabled={disabled}
          pageOutcome={pageOutcome}
          readOnly={readOnly}
          register={register}
          score={score}
          setScore={setScore}
        />
      </div>

      {rubricLevel && (
        <div
          className={clsx(
            "rubric-score-description p-2",
            rubricLevel && scoreDescriptionBackgroundColor(rubricLevel.score),
            disabled && "disabled",
          )}
        >
          <div className={clsx("title mr-2 flex gap-2", rubricLevel.description && "mb-2")}>
            <ScoreIcon disabled={disabled} size="s" value={rubricLevel.score} />
            <h4 className="m-0">{rubricLevel.name}</h4>
          </div>
          {rubricLevel.description ? (
            <div className="body-s mb-1 ml-6 mr-1 flex">{rubricLevel.description}</div>
          ) : null}
        </div>
      )}
    </div>
  );
};

interface RubricButtonsProps {
  pageOutcome: NestedPageOutcomeType;
  score: number | null;
  setScore: (pageOutcomeId: string, score: number | null) => void;
  register: UseFormRegisterReturn;
  disabled?: boolean;
  readOnly?: boolean;
}

export const RubricButtons: FC<RubricButtonsProps> = ({
  pageOutcome,
  score,
  setScore,
  register,
  readOnly = false,
  disabled = false,
}) => {
  // TODO: this is very similar to the RubricButtons component used by AssessmentDisplayView.
  // We should consolidate these implementations. (JN)
  const rubric = useAppSelector((s) => selectPageOutcomeRubric(s, pageOutcome.id));

  return (
    <div className="flex rounded-md bg-black-tint-95">
      {rubric.map((rubricLevel) => (
        <SquareIconButton
          aria-label={`${t("glossary.score")} ${rubricLevel.score}`}
          className="!p-0"
          disabled={disabled || readOnly}
          key={rubricLevel.score}
          kind="ghost"
          size="s"
          onClick={() => {
            const newScore = rubricLevel.score === score ? NaN : rubricLevel.score;
            setScore(pageOutcome.id, newScore);
            trackEvent("Assessment - Select outcome score", {
              ...BUTTON_EVENT_CATEGORY,
              outcome: pageOutcome.outcome.title,
              score: newScore,
            });
          }}
          {...register}
        >
          <ScoreIcon
            disabled={disabled}
            size="l"
            unselected={score !== rubricLevel.score}
            value={rubricLevel.score}
          />
        </SquareIconButton>
      ))}
    </div>
  );
};
